import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getStudentPlanMeta } from '../lib/firestore/studentPlan';
import { saveStudentPlanMeta, logAuditEvent } from '../lib/firestore/studentPlan';
import { triageToNeedScore, scoreToPath } from '../lib/scoring';
import DecisionSummary from '../components/DecisionSummary';
import ChangePathDialog from '../components/ChangePathDialog';
import AppLayout from '../components/AppLayout';
import toast from 'react-hot-toast';

const DecisionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [student, setStudent] = useState(null);
  const [planMeta, setPlanMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [triageAnswers, setTriageAnswers] = useState(null);
  const [isDirectSelection, setIsDirectSelection] = useState(false);
  const [selectedPath, setSelectedPath] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch student data
        const studentRef = doc(db, 'students', id);
        const studentSnap = await getDoc(studentRef);
        
        if (!studentSnap.exists()) {
          navigate('/students');
          return;
        }
        
        const studentData = { id: studentSnap.id, ...studentSnap.data() };
        setStudent(studentData);
        
        // Fetch plan metadata
        const meta = await getStudentPlanMeta(id);
        setPlanMeta(meta);
        
        // Check if coming from triage or direct selection
        if (location.state?.fromTriage) {
          setTriageAnswers(location.state.triageAnswers);
          setIsDirectSelection(false);
        } else if (location.state?.isDirectSelection) {
          setSelectedPath(location.state.selectedPath);
          setIsDirectSelection(true);
        } else {
          // Load existing triage answers if available
          if (meta?.triageAnswers) {
            const rawScores = meta.triageAnswers.map(answer => answer.raw ?? answer.score ?? 1);
            setTriageAnswers(rawScores);
            setIsDirectSelection(false);
          }
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error loading data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, location.state]);

  const handleConfirmPath = async () => {
    try {
      const needScore = triageAnswers ? triageToNeedScore(triageAnswers) : null;
      const path = selectedPath || (needScore !== null ? scoreToPath(needScore) : null);
      
      if (!path) {
        toast.error('No path selected');
        return;
      }

      // Save to Firestore
      await saveStudentPlanMeta(id, {
        curriculumPath: path,
        decisionScore: needScore, // 0–12, higher = higher need
        decisionSource: isDirectSelection ? 'MANUAL_OVERRIDE' : 'TRIAGE',
        lastUpdatedByUid: 'current-user-id', // TODO: Get from auth
        lastUpdatedAt: new Date().toISOString()
      });

      // Log audit event
      await logAuditEvent(id, {
        event: 'PATH_SET',
        actorUid: 'current-user-id', // TODO: Get from auth
        timestamp: new Date().toISOString(),
        payload: {
          path: path,
          decisionSource: isDirectSelection ? 'MANUAL_OVERRIDE' : 'TRIAGE',
          needScore: needScore
        }
      });

      toast.success('Path confirmed successfully');
      navigate(`/students/${id}/interventions-builder`);
      
    } catch (error) {
      console.error('Error confirming path:', error);
      toast.error('Error confirming path');
    }
  };

  const handleOverride = () => {
    setShowOverrideDialog(true);
  };

  const handleOverrideConfirm = async (newPath) => {
    try {
      setSelectedPath(newPath);
      setIsDirectSelection(true);
      setShowOverrideDialog(false);
      
      // Save the override
      await saveStudentPlanMeta(id, {
        curriculumPath: newPath,
        decisionSource: 'MANUAL_OVERRIDE',
        lastUpdatedByUid: 'current-user-id', // TODO: Get from auth
        lastUpdatedAt: new Date().toISOString()
      });

      toast.success('Path overridden successfully');
      
    } catch (error) {
      console.error('Error overriding path:', error);
      toast.error('Error overriding path');
    }
  };

  const handleBack = () => {
    if (location.state?.fromTriage) {
      navigate(`/students/${id}/triage`);
    } else {
      navigate(`/students/${id}`);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </AppLayout>
    );
  }

  if (!student) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Student Not Found</h1>
            <p className="text-gray-600 mb-4">The student you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate('/students')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Back to Students
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  // If direct selection, show simplified confirmation
  if (isDirectSelection && selectedPath) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Confirm Path Selection
            </h1>
            <p className="text-gray-600">
              Confirm the curriculum path for {student.firstName} {student.lastName}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Selected Path: {selectedPath === 'NC_ONLY' ? 'National Curriculum Only' : 
                               selectedPath === 'NC_WITH_SUPPORTS' ? 'National Curriculum with Supports' : 
                               'Bespoke Curriculum'}
              </h2>
              <p className="text-gray-600">
                {selectedPath === 'NC_ONLY' 
                  ? 'This student will access the national curriculum without additional supports required.'
                  : selectedPath === 'NC_WITH_SUPPORTS' 
                  ? 'This student will access the national curriculum with targeted interventions and support strategies.'
                  : 'This student will receive a custom-designed curriculum tailored to their individual needs and learning profile.'
                }
              </p>
            </div>

            <div className="flex justify-between items-center">
              <button
                onClick={handleBack}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back
              </button>
              
              <button
                onClick={handleConfirmPath}
                className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Confirm Path
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Show triage results
  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Assessment Results
          </h1>
          <p className="text-gray-600">
            Review the triage assessment results for {student.firstName} {student.lastName}
          </p>
        </div>

        {triageAnswers && (
          <DecisionSummary
            answers={triageAnswers}
            onConfirm={handleConfirmPath}
            onOverride={handleOverride}
            onBack={handleBack}
          />
        )}

        <ChangePathDialog
          isOpen={showOverrideDialog}
          onClose={() => setShowOverrideDialog(false)}
          onConfirm={handleOverrideConfirm}
          currentPath={planMeta?.curriculumPath}
          recommendedPath={triageAnswers ? scoreToPath(triageToNeedScore(triageAnswers)) : null}
        />
      </div>
    </AppLayout>
  );
};

export default DecisionPage;
